// src/components/dashboard/widgets/nearby-transport-picker.tsx
import { useEffect, useRef, useState } from 'react';
import { X, Search } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

export interface NearbyStop { id: string; name: string; type: string }

interface NearbyTransportPickerProps {
  value: NearbyStop[];
  onChange: (stops: NearbyStop[]) => void;
}

export default function NearbyTransportPicker({ value, onChange }: NearbyTransportPickerProps) {
  const c = useAppColors();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NearbyStop[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const { data } = await supabase.functions.invoke('stop-finder', { body: { query: query.trim() } });
      setLoading(false);
      setResults(data?.ok ? (data.results as NearbyStop[]) : []);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  function addStop(stop: NearbyStop) {
    if (value.some((s) => s.id === stop.id)) return;
    onChange([...value, stop]);
    setQuery('');
    setResults([]);
  }

  function removeStop(id: string) {
    onChange(value.filter((s) => s.id !== id));
  }

  return (
    <div className="space-y-1.5 ">
      <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: c.textFaint }}>Nearby stations / stops (optional)</label>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5 ">
          {value.map((s) => (
            <span key={s.id} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full" style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}`, color: c.accentSoft }}>
              {s.name}
              <button type="button" onClick={() => removeStop(s.id)}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: c.textFaint }} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a station, wharf, or stop…"
          className="w-full h-11 rounded-lg pl-9 pr-3 text-sm outline-none"
          style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}
        />
        {(results.length > 0 || loading) && (
          <div className="absolute z-10 mt-1 w-full rounded-lg overflow-hidden" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
            {loading ? (
              <div className="px-3 py-2 text-xs" style={{ color: c.textFaint }}>Searching…</div>
            ) : (
              results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => addStop(r)}
                  className="w-full text-left px-3 py-2 text-sm"
                  style={{ color: c.text }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = c.iconBg; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                >
                  {r.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}